import React from 'react';
import { Box, Typography, Checkbox } from '@mui/material';
import CurrencyInput from './CurrencyInput.jsx';

const DynamicFeeRenderer = ({ 
  attributes = [], 
  values = {}, 
  onChange, 
  enabledValues = {},
  onEnabledChange,
  isLoading = false 
}) => {
  const handleAttributeChange = (attributeId, value) => {
    onChange(attributeId, value);
  };

  const handleEnabledChange = (attributeId, enabled) => {
    onEnabledChange(attributeId, enabled);
  };

  const renderAttribute = (attribute) => {
    // Validate attribute has valid ID
    if (!attribute.id || attribute.id === 'null' || attribute.id === 'undefined') {
      console.error('❌ DynamicFeeRenderer: Invalid attribute ID:', attribute);
      return null;
    }
    
    // Handle both old format (string value) and new format (object with value and uuid)
    const valueOrObject = values[attribute.id];
    const value = typeof valueOrObject === 'object' ? valueOrObject.value : (valueOrObject || '0.0000');
    const enabled = enabledValues[attribute.id] || false;
    // Convert UUID to use underscores and wrap in attributes array format
    const formName = `attributes[${String(attribute.id).replace(/-/g, '_')}]`;

    // Check if this attribute should have a checkbox (based on attribute type or other criteria)
    // For now, we'll assume attributes with type 'fee_with_checkbox' need checkboxes
    // and others are just simple currency inputs
    const needsCheckbox = attribute.type === 'fee_with_checkbox' ||
                         attribute.needs_checkbox === true ||
                         // Default to checkbox for backward compatibility unless explicitly disabled
                         (attribute.type !== 'currency' && attribute.needs_checkbox !== false);

    if (needsCheckbox) {
      return (
        <Box key={attribute.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={enabled}
              onChange={(e) => handleEnabledChange(attribute.id, e.target.checked)}
              size="small"
            />
            <Typography variant="body2">{attribute.content}</Typography>
          </Box>
          <CurrencyInput
            name={formName}
            value={value}
            onChange={(newValue) => handleAttributeChange(attribute.id, newValue)}
            placeholder="0.0000"
            width={120}
            disabled={!enabled}
          />
        </Box>
      );
    } else {
      // Simple currency input without checkbox - always enabled
      return (
        <Box key={attribute.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">{attribute.content}</Typography>
          <CurrencyInput
            name={formName}
            value={value}
            onChange={(newValue) => handleAttributeChange(attribute.id, newValue)}
            placeholder="0.0000"
            width={120}
            disabled={false}
          />
        </Box>
      );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Loading dynamic fees...
        </Typography>
      </Box>
    );
  }

  if (!attributes || attributes.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {attributes.map(renderAttribute)}
    </Box>
  );
};

export default DynamicFeeRenderer;
